import { useEffect, useState } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const PayrollDeduction = () => {
  const navigate = useNavigate();
  const itemId = "payroll-deduction";
  const fieldOrder = ["date", "employeeName", "employeeSignature"];
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    }
  }, []);

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const statusToPersist = allEmpty ? "incomplete" : (existing === "complete" ? "complete" : "in-progress");
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (accountId && statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData]);

  const handleSubmit = async () => {
    const accountId = localStorage.getItem("accountId");
    if (!accountId) {
      return navigate("/register");
    }

    // Persist locally for fast reload
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));

    // Derive status from filled fields
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const status = allEmpty ? "incomplete" : allFilled ? "complete" : "in-progress";
    const progress = status === "complete" ? 100 : status === "in-progress" ? 50 : 0;

    try {
      await axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, {
        status,
        progress,
        formData,
      });
    } catch (err) {
      console.error("Failed to save Payroll Deduction form to server:", err);
      // keep existing UX: still return to checklist
    }

    if (status === "complete") {
      showSuccess("Form completed successfully!");
    }
    navigate("/register");
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        {/* Top content card */}
        <div className="form-card">
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
              <h2 className="form-title" style={{ textAlign: "center" }}>
                Voluntary Payroll Deduction Authorization
              </h2>
          </header>

          <section className="form-card-content">
            <p className="form-intro">
              Employer Name: Talent Employment Inc.
            </p>
            <p className="form-intro paragraph-space">
              I hereby authorize my employer to make any of the deductions below as used from my pay in accordance to receiving any of the following items from Talent Employment Inc
            </p>
            <ul className="form-bullets">
              <li>• Carpool</li>
              <li>• Uniforms (T-shirts, dress shirts, vests, etc)</li>
              <li>• Trainings (Basic server, food handlers, Smart Serve, etc)</li>
              <li>• Accommodations and/or transportation</li>
              <li>• Payroll discrepancies such as overpayments</li>
              <li>• Criminal background checks</li>
            </ul>
            <p className="form-intro paragraph-space">
              I understand and agree that I am responsible for satisfying any of the above amounts. I understand and agree that any amount that is due and owing at the time of my termination, regardless of whether my termination was voluntary or not, will be deducted from my last paycheck or any other amount owing to me. I understand that should I not fulfill my employment with Talent, the certification attained will not be available to me until the amount has been settled and paid out of pocket.
            </p>
          </section>
          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="employeeName">Employee's Full Name*</label>
              <input
                id="employeeName"
                type="text"
                value={formData.employeeName}
                onChange={(e) =>
                  handleInputChange("employeeName", e.target.value)
                }
                required
              />
            </div>
          </div>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            {/* <label>Employee's Signature*</label> */}
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit}>Submit</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollDeduction;
